import { Component, Ref } from 'vue-property-decorator'
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader.vue'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import { GetContextMenuItemsParams, MenuItemDef, RowClickedEvent } from '@ag-grid-enterprise/all-modules'
import { BaseCrud } from '@/shared/models/cruds.models'
import BaseCrudServerSideComponent from '@/shared/resources/base-crud-server-side.component'
import { ConfigurationBillableConceptCreateRequest, ConfigurationBillableConceptPatchInBatchRequest, ConfigurationBillableConceptResponse } from '@/modules/configurationModule/detail/accounting/billable/concepts/services/configuration-billable-concept.dto'
import {
    BillableConceptGridState,
    ConfigurationBillableConceptsGridSidepanelComboElements,
    initialBillableConceptGridState
} from '@/modules/configurationModule/detail/accounting/billable/concepts/components/grid/billable-concept-grid.model'
import { configurationBillableConceptService } from '@/modules/configurationModule/detail/accounting/billable/concepts/services/configuration-billable-concept.service'
import ConceptsModalBasicForm from '@/modules/configurationModule/detail/accounting/billable/concepts/components/modal-basic-form/concepts-modal-basic-form.vue'
import {
    ConfigurationListSidePanelEntity
} from '@/modules/configurationModule/shared/components/configuration-list-sidepanel/configuration-list-sidepanel.model'
import ConfigurationListSidepanelComp
    from '@/modules/configurationModule/shared/components/configuration-list-sidepanel/configuration-list-sidepanel'
import ConfigurationListSidepanel
    from '@/modules/configurationModule/shared/components/configuration-list-sidepanel/configuration-list-sidepanel.vue'
import { CommonParams } from '@/api/resources/common/shared/models/common.models'
import { PAGINATOR_BIG_PAGE_SIZE } from '@/shared/services/services-constants'
import { GridUtils } from '@/shared/util/grid-utils'
import { billableRateBillableConceptService } from '@/modules/configurationModule/detail/local-accounting/billable/rates/services/billable-rate-billable-concept/billable-rate-billable-concept.service'
import { BillableRateBillableConceptResponse } from '@/modules/configurationModule/detail/local-accounting/billable/rates/services/billable-rate-billable-concept/billable-rate-billable-concept.dto'
import { ToastUtils } from '@/shared/util/toast-utils'

@Component({
    components: {
        ConfigurationSectionHeader,
        VtGrid,
        ConceptsModalBasicForm,
        ConfigurationListSidepanel
    }
})
export default class BillableConceptGrid extends BaseCrudServerSideComponent<ConfigurationBillableConceptResponse> implements BaseCrud {
    @Ref() readonly billableConceptModalBasicFormWrapper!: any
    @Ref() readonly configurationListSidepanel!: ConfigurationListSidepanelComp

    state: BillableConceptGridState
    constructor() {
        super()
        this.state = initialBillableConceptGridState()
    }

    get cantUpdate(): boolean {
        return !this.canUpdate
    }

    get canUpdate(): boolean {
        return this.state.gridApi?.getSelectedRows().length === 1
    }

    get cantDelete(): boolean {
        return !this.canDelete
    }

    get canDelete(): boolean {
        return !!this.state.gridApi?.getSelectedRows().length
    }

    get canActivateBatch():boolean {
        return this.hasDeactiveEntity && !this.hasAllActivated
    }

    get hasDeactiveEntity():boolean {
        return !!this.state.selectedRows.find(entity => !entity.active)
    }

    get canDeactivateBatch():boolean {
        return this.state.selectedRows.length > 0 && this.hasActivePriority && !this.hasAllDeactivated
    }

    get hasActivePriority():boolean {
        return !!this.state.selectedRows.find(entity => entity.active)
    }

    getContextMenuItems(params: GetContextMenuItemsParams): MenuItemDef[] {
        if (!params?.node) {
            return []
        } else {
            return [
                {
                    name: this.$tc('configuration.actions.update'),
                    cssClasses: this.state.selectedRows.length > 1 ? ['not-show'] : [''],
                    action: () => this.onUpdate()
                },
                {
                    name: this.$tc('configuration.actions.delete'),
                    cssClasses: this.state.selectedRows.length < 1 ? ['not-show'] : [''],
                    action: () => this.deleteOperation(this.state.selectedRows)
                },
                {
                    name: this.$tc('configuration.actions.activate'),
                    cssClasses: this.canActivateBatch ? [''] : ['not-show'],
                    action: () => {
                        this.activateDeactivateSelection(true, this.state.selectedRows)
                    }
                },
                {
                    name: this.$tc('configuration.actions.deactivate'),
                    cssClasses: this.canDeactivateBatch ? [''] : ['not-show'],
                    action: () => {
                        this.activateDeactivateSelection(false, this.state.selectedRows)
                    }
                }
            ]
        }
    }

    async activateDeactivateSelection(option: boolean, selection: ConfigurationBillableConceptResponse[]): Promise<void> {
        const selectionToModify = selection.filter(selected => selected.active !== option).map(selected => {
            selected.active = option
            return selected
        })
        const result = await this.$_toCallSecureLoading(configurationBillableConceptService.patch(selectionToModify))
        if (result) {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated'))
            this.reloadData()
        }
    }

    onAdd(): void {
        this.showModal(this.$tc('configuration.actions.add'), null, this.billableConceptModalBasicFormWrapper)
    }

    onUpdate(): void {
        this.showModal(this.$tc('configuration.actions.update'), this.state.selectedRows[0], this.billableConceptModalBasicFormWrapper)
    }

    async createOperation(row: ConfigurationBillableConceptCreateRequest): Promise<void> {
        const result = await this.$_toCallSecureLoading(configurationBillableConceptService.create(row))
        if (result) {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.created'))
            this.billableConceptModalBasicFormWrapper.hide()
            this.reloadData()
        }
    }

    async patchOperation(row: ConfigurationBillableConceptPatchInBatchRequest): Promise<void> {
        const result = await this.$_toCallSecureLoading(configurationBillableConceptService.patch([row]))
        if (result?.length !== 1) {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated'))
            this.billableConceptModalBasicFormWrapper.hide()
            this.reloadData()
        }
    }

    async deleteSelectedRows(): Promise<void> {
        this.deleteOperation(this.state.selectedRows)
    }

    onRowClicked(event: RowClickedEvent): void {
        const customerGroupSelected: ConfigurationBillableConceptResponse = event.data
        this.configurationListSidepanel.show({
            title: customerGroupSelected.name,
            entities: [
                {
                    id: ConfigurationBillableConceptsGridSidepanelComboElements.BILLABLE_RATES,
                    entityKey: 'configuration.sections.rate'
                }
            ],
            obtainEntityList: async (entity: ConfigurationListSidePanelEntity): Promise<string[]> => {
                if (entity.id as ConfigurationBillableConceptsGridSidepanelComboElements === ConfigurationBillableConceptsGridSidepanelComboElements.BILLABLE_RATES) {
                    return this._getBillableRates(customerGroupSelected.id)
                }
                return []
            }
        })
    }

    private async _getBillableRates(conceptId: number): Promise<string[]> {
        const params: CommonParams = {
            size: PAGINATOR_BIG_PAGE_SIZE
        }
        const filterModel = {
            'concept.id': GridUtils.getNumberFilterModel('equals', conceptId)
        }
        const laboratoryId = this.$store.getters.labId
        const response = await this.$_toCallUnique(billableRateBillableConceptService.paginator(filterModel, params, laboratoryId))
        return response?.content.map((item:BillableRateBillableConceptResponse) => item.rate.name) ?? []
    }

    async deleteOperation(concepts: ConfigurationBillableConceptResponse[]): Promise<void> {
        if (concepts.length) {
            const selectedRowsSize: number = this.state?.selectedRows?.length
            const labelConfirmation: string = this.$tc('configuration.grid.deleteConfirmation')
            const labelGrid: string = this.$tc('configuration.grid.element', selectedRowsSize)
            const labelDeleteQuestion: string = this.$tc('configuration.grid.deleteQuestion')
            const labelDeleteAction: string = this.$tc('configuration.actions.delete')
            const deleteConfirmation = await this.$_showConfirmationModal(
                `${labelConfirmation} ${selectedRowsSize} ${labelGrid}, <br/> ${labelDeleteQuestion}`,
                labelDeleteAction
            )

            if (deleteConfirmation) {
                const toRemove = {
                    entityIdentifiers: concepts.map(concept => concept.id)
                }
                const response = await this.$_toCallSecureLoading(configurationBillableConceptService.delete(toRemove))
                if (response) {
                    const elements = concepts.length - response.length
                    if (elements > 0) {
                        const choice = elements > 1 ? 2 : 1
                        ToastUtils.showToastSuccess(this.$tc('configuration.toastMessages.deleted', choice, { elements }))
                        this.reloadData()
                    }
                }
            }
        }
    }

    closeSidePanel():void {
        this.state.showSidepanel = false
    }
}
